import React, { useEffect, useMemo, useRef, useState } from "react";
import { Tab, TabGroup, TabPanel, TabPanels } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import Patient from "../../Components/Patient";
import {
  useGoodThoughtsControllerGetPatientsForGtSessionBySessionIdQuery,
  useGoodThoughtsControllerSummaryEmojisQuery,
  useGoodThoughtsControllerSummaryEmojisByPatientsQuery,
  useGoodThoughtsControllerGetQuestionsForGtSessionBySessionIdQuery,
  useGoodThoughtsControllerLastXDaysPatientsAnswersQuery,
  useGoodThoughtsControllerGetTranscriptionMutation,
  apiSlice,
} from "../../Features/API/apiSlice.generated";

import { useNotification } from "../../Hooks/useNotification";
import { NotificationType } from "../../Constants/notificationType.enum";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { useAppDispatch } from "../../Store/store";
import { response } from "express";
import ReactMarkdown from "react-markdown";
import classNames from "classnames";
import Notes from "../../Components/PatientNotes";

const SummaryPage = () => {
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [patientNotes, setPatientNotes] = useState("");
  const currentDate = new Date();
  const dispatch = useAppDispatch();
  const date = currentDate.toLocaleDateString();
  const time = currentDate.toLocaleTimeString();

  const { notify } = useNotification();

  const urlParams = new URLSearchParams(window.location.search);
  const participants = urlParams.get("participants");
  const sessionId: any = urlParams.get("sessionId");
  const meetId: any = urlParams.get("meetingId");
  const [transcriptionString, setTranscriptionString] = useState("");
  const [transcriptionConversation, setTranscriptionConversation] =
    useState("");
  const patientIds = participants?.split(",");
  // const [count, setCount] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    notify(
      "Please refresh the page if any patient information is missing.",
      NotificationType.WARNING
    );
  }, []);

  const {
    data: patients,
    isLoading: patientsLoading,
    isError: patientsError,
  } = useGoodThoughtsControllerGetPatientsForGtSessionBySessionIdQuery({
    sessionId: sessionId,
  });

  const {
    data: sessionQuestions,
    isLoading: questionsLoading,
    isError: questionsErrors,
  } = useGoodThoughtsControllerGetQuestionsForGtSessionBySessionIdQuery({
    sessionId: sessionId,
  });

  const {
    data: sessionAnswerXDays,
    isLoading: sessionAnswerXDaysLoading,
    isError: sessionAnswerXDaysErrors,
  } = useGoodThoughtsControllerLastXDaysPatientsAnswersQuery({
    sessionId: sessionId,
    numberOfDays: 5,
  });
  const summaryEmoji = useGoodThoughtsControllerSummaryEmojisQuery({
    sessionId: sessionId,
  });

  const {
    data: emojiUsedByPatient,
    isLoading: emojiUsedByPatientLoading,
    isError: emojiUsedByPatientErrors,
  } = useGoodThoughtsControllerSummaryEmojisByPatientsQuery({
    sessionId: sessionId,
  });

  const [getTranscription, { data, isLoading, error }] =
    useGoodThoughtsControllerGetTranscriptionMutation();

  const hasFetched = useRef(false);
  useEffect(() => {
    if (transcriptionString !== "" || hasFetched.current) {
      return;
    }
    console.error("fetching API ");
    hasFetched.current = true;
    getTranscription({
      videoSdkdto: {
        meetingId: meetId,
        sessionId: sessionId,
      },
    })
      .unwrap()
      .then((responseData) => {
        var object: any = responseData;
        // console.log("AI backend reply: ", object.response);
        if (object?.response !== "") {
          setTranscriptionString(object?.response);
          setPatientNotes(object?.response);
        }

        if (object?.transcriptionText) {
          setTranscriptionConversation(object?.transcriptionText);
        }
      })
      .catch((err) => {
        setTranscriptionString(
          "Unable to get AI transcription you have hit the rate limit, try again in 10 minutes."
        );
        console.log("Unable to get transcription!", err);
        return;
      });
  }, [transcriptionString]);

  const patientPastXDaysData = useMemo(() => {
    let patientData =
      patients &&
      patients.map((item: any) => {
        const fiveDaysPatientAnswer =
          sessionAnswerXDays &&
          sessionAnswerXDays.find(
            (innerItem) => item?._id === innerItem.patientId
          );
        const emojiByPatient =
          emojiUsedByPatient &&
          emojiUsedByPatient.find(
            (innerItem) => innerItem.userId === item.user
          );
        return {
          ...item,
          answers: fiveDaysPatientAnswer ? fiveDaysPatientAnswer.answers : [],
          emoji: emojiByPatient?.emojis,
        };
      });
    return patientData || [];
  }, [patients, sessionAnswerXDays]);

  const handleHomeRoute = () => {
    navigate("/", { replace: true });
  };

  const groupOptions = {
    filename: `Group_summary_${date}.pdf`,
    page: {
      margin: 10,
      orientation: "portrait",
    },
  };
  const pdfRef = useRef(null);

  const downloadGroupPdf = async () => {
    // eslint-disable-next-line no-restricted-globals
    print();
    return;
    setLoading(true);
    try {
      const pdf = new jsPDF("portrait", "pt", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Group notes section (new page)
      const notesSection = document.querySelector("#group-notes");
      if (notesSection) {
        pdf.addPage();
        const notesCanvas = await html2canvas(notesSection as HTMLElement, {
          scale: 2,
          logging: false,
          useCORS: true,
        });
        const notesImgData = notesCanvas.toDataURL("image/png");
        const notesHeight = (notesCanvas.height * pdfWidth) / notesCanvas.width;
        pdf.addImage(notesImgData, "PNG", 20, 50, pdfWidth - 40, notesHeight);
      }

      pdf.save(`Group_summary_${date}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      notify("Error generating PDF", NotificationType.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const downloadPatientPdf = async (patientElement: HTMLElement) => {
    setLoading(true);
    try {
      const pdf = new jsPDF("portrait", "pt", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      let currentY = 0;

      // Header section
      const headerSection = document.querySelector(".summary-header");
      if (headerSection) {
        const headerCanvas = await html2canvas(headerSection as HTMLElement, {
          scale: 2,
          logging: false,
          useCORS: true,
          windowWidth: headerSection.scrollWidth,
          windowHeight: headerSection.scrollHeight,
          y: -10,
          height: (headerSection as HTMLElement).offsetHeight + 20,
        });
        const headerImgData = headerCanvas.toDataURL("image/png");
        const headerHeight =
          (headerCanvas.height * pdfWidth) / headerCanvas.width;
        pdf.addImage(
          headerImgData,
          "PNG",
          20,
          currentY,
          pdfWidth - 40,
          headerHeight + 5
        );
        currentY += headerHeight + 10;
      }

      // Patient section
      const canvas = await html2canvas(patientElement, {
        scale: 2,
        logging: false,
        useCORS: true,
      });
      const imgData = canvas.toDataURL("image/png");
      const patientHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, currentY, pdfWidth, patientHeight);

      pdf.save(`Patient_summary_${date}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      notify("Error generating PDF", NotificationType.ERROR);
    } finally {
      setLoading(false);
    }
  };

  if (patientsLoading || patients === null || patientPastXDaysData === null) {
    return <div>Loading...</div>;
  }

  if (patientsError) {
    return <div>Error loading patients</div>;
  }

  // console.log("transcription string: ", transcriptionString)

  const filteredPatients = !patientsLoading && patients && patientPastXDaysData;

  if (isLoading) {
    return (
      <pre>
        <div role="status" className="text-right">
          <svg
            aria-hidden="true"
            className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
          <span className="sr-only">Loading...</span>
        </div>

      </pre>
    );
  }

  return (
    <>
      <div id="print-group-container" className="mx-auto w-full" ref={pdfRef}>
        <div className="summary-header">
          <h1 className="text-3xl font-bold mb-4">Summary</h1>
          <p className="my-1 font-semibold"> Date: {date} </p>
          <p className="mb-4 font-semibold"> Time: {time} </p>
        </div>
        <TabGroup
          onChange={setSelectedIndex}
          selectedIndex={selectedIndex}
          defaultIndex={0}
          className="items-center text-center"
        >
          <Tab
            className={({ selected }) =>
              classNames(
                "bg-primary border-primary border capitalize font-primary py-4 px-2 md:px-10 font-bold tracking-widest transition ease-in duration-100 text-xs md:text-lg no-print",
                selected
                  ? "hover:bg-activeWhite text-white"
                  : "text-secondary bg-white drop-shadow-activeText"
              )
            }
          >
            Edit Summary
          </Tab>
          <Tab
            className={({ selected }) =>
              classNames(
                "bg-primary border-primary border capitalize font-primary py-4 px-2 md:px-10 font-bold tracking-widest transition ease-in duration-100 text-xs md:text-lg no-print",
                selected
                  ? "hover:bg-activeWhite text-white"
                  : "text-secondary bg-white drop-shadow-activeText"
              )
            }
          >
            Transcription
          </Tab>
          <Tab
            className={({ selected }) =>
              classNames(
                "bg-primary border-primary border capitalize font-primary py-4 px-2 md:px-10 font-bold tracking-widest transition ease-in duration-100 text-xs md:text-lg no-print",
                selected
                  ? "hover:bg-activeWhite text-white"
                  : "text-secondary bg-white drop-shadow-activeText"
              )
            }
          >
            Full Report
          </Tab>
          <TabPanels>
            <TabPanel className="text-left">
              {" "}
              <div className="flex flex-col w-full my-4" id="group-notes">
                <label className="text-lg font-semibold mb-4" title="notes">
                  {" "}
                  Feel free to add any additional notes to the session below,
                  then print the summary
                </label>

                <textarea
                  className="border border-gray-200 p-2 rounded-md whitespace-pre-line min-h-64"
                  name="notes"
                  id="notes"
                  style={{ resize: "none" }}
                  value={patientNotes}
                  onChange={(e) => setPatientNotes(e.target.value)}
                ></textarea>
              </div>
            </TabPanel>
            <TabPanel className="text-left">
              <div className="flex flex-col w-full my-4" id="group-notes">
                <label className="text-lg font-semibold mb-4">
                  {" "}
                  Below is a transcription of the session, please note it isn't
                  always accurate.
                </label>
                <div className="border border-gray-200 p-2 rounded-md whitespace-pre-line">
                  {transcriptionConversation}
                </div>
                <br />
              </div>
            </TabPanel>

            <TabPanel className="text-left">
              <b>Patient Information</b>
              {filteredPatients &&
                filteredPatients.map(
                  (patient: any) =>
                    patient.answers.length > 0 && (
                      <>
                        <div className="patient-section" key={patient?.id}>
                          <Patient
                            id={patient?.user}
                            patientAnswers={patient?.answers}
                            sessionQuestions={sessionQuestions}
                            downloadPatientPdf={downloadPatientPdf}
                            loading={loading}
                            reportDateAndTime={patient?.updatedAt}
                            emojiByPatient={patient?.emoji}
                          />
                        </div>
                      </>
                    )
                )}
              <div
                className="whitespace-pre-wrap break-words flex flex-col w-full my-4"
                style={{ pageBreakBefore: "always" }}
                id="group-notes-final"
              >
                <br />
                <h3>
                  <b>Doctor's Notes</b>
                </h3>{" "}
                <div className="bg-gray-100 rounded-lg shadow-md p-8 mb-10 flex flex-col justify-center">
                  <Notes text={patientNotes} />
                  {/* <div className="[&_ul]:my-1 [&_li]:my-0 [&_p]:my-1">
                    <ReactMarkdown>{patientNotes}</ReactMarkdown>
                  </div> */}
                  <p className="no-print">
                    <br />
                    <hr />
                    <br />
                    <b>You can edit this summary in the "Edit Summary" tab above.</b>
                  </p>
                </div>
              </div>
            </TabPanel>
          </TabPanels>
        </TabGroup>
      </div>
      <div className="flex justify-end items-center gap-3 no-print">
        <button
          onClick={downloadGroupPdf}
          disabled={loading}
          className={`h-16 w-[16rem] text-sm bg-primary text-white font-semibold rounded-lg py-3 px-4 my-3 ${loading && "opacity-80"}`}
        >
          {loading ? "Downloading..." : "Print Group Summary"}
        </button>
        <button
          onClick={handleHomeRoute}
          className="h-16 w-[16rem] text-sm bg-primary font-semibold text-white py-3 px-4 rounded-lg my-3 text-center"
        >
          Go to Homepage
        </button>
      </div>
    </>
  );
};

export default SummaryPage;
